import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  User, 
  Mail, 
  Lock, 
  Users, 
  Shield,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import AppShell from '../../components/AppShell';
import Guard from '../../components/Guard';
import useAdminStore from '../../store';

const CreateUserPage = () => {
  const navigate = useNavigate();
  const { 
    groups,
    permissions,
    loading,
    errors,
    fetchGroups,
    fetchPermissions,
    createUser 
  } = useAdminStore();

  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);

  // Form data
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    email: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    
    // Step 2: Groups (mandatory)
    groups: [],
    
    // Step 3: Additional Permissions (optional)
    additionalPermissions: []
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchGroups();
    fetchPermissions();
  }, [fetchGroups, fetchPermissions]);

  // Step validation functions
  const validateStep1 = () => {
    const errors = {};
    
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.displayName) {
      errors.displayName = 'Name is required';
    } else if (formData.displayName.length < 2) {
      errors.displayName = 'Name must be at least 2 characters';
    }
    
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }
    
    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    return errors;
  };

  const validateStep2 = () => {
    const errors = {};
    
    if (formData.groups.length === 0) {
      errors.groups = 'At least one user group must be selected';
    }
    
    return errors;
  };

  const validateStep3 = () => {
    // Step 3 is optional, so no validation errors
    return {};
  };

  // Navigation functions
  const canProceed = (step) => {
    const validators = {
      1: validateStep1,
      2: validateStep2,
      3: validateStep3
    };
    
    const errors = validators[step]();
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (canProceed(currentStep)) {
      if (!completedSteps.includes(currentStep)) {
        setCompletedSteps([...completedSteps, currentStep]);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    setValidationErrors({});
  };

  const handleStepClick = (step) => {
    if (step <= currentStep || completedSteps.includes(step - 1)) {
      setCurrentStep(step);
      setValidationErrors({});
    }
  };

  // Form handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleGroupToggle = (groupId) => {
    const updatedGroups = formData.groups.includes(groupId)
      ? formData.groups.filter(id => id !== groupId)
      : [...formData.groups, groupId];
    
    handleInputChange('groups', updatedGroups);
  };

  const handlePermissionToggle = (permissionKey) => {
    const updatedPermissions = formData.additionalPermissions.includes(permissionKey)
      ? formData.additionalPermissions.filter(key => key !== permissionKey)
      : [...formData.additionalPermissions, permissionKey];
    
    handleInputChange('additionalPermissions', updatedPermissions);
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!canProceed(3)) return;
    
    setIsSubmitting(true);
    
    try {
      const userData = {
        email: formData.email,
        displayName: formData.displayName,
        password: formData.password,
        groups: formData.groups,
        permissions: formData.additionalPermissions,
        status: 'active'
      };
      
      await createUser(userData);
      
      // Navigate back to users list
      navigate('/admin-iam/users', { 
        state: { 
          message: `User ${formData.displayName} created successfully!`,
          type: 'success'
        }
      });
      
    } catch (error) {
      console.error('Failed to create user:', error);
      setValidationErrors({
        submit: error.message || 'Failed to create user. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step component renderers
  const renderStep1 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Basic Information</h3>
        <p className="text-sm text-gray-600 mb-6">
          Enter the user's basic details and credentials
        </p>
      </div>

      <div className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address *
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="user@example.com"
            />
          </div>
          {validationErrors.email && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {validationErrors.email}
            </p>
          )}
        </div>

        {/* Display Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name *
          </label>
          <div className="relative">
            <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.displayName ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="John Doe"
            />
          </div>
          {validationErrors.displayName && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {validationErrors.displayName}
            </p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.password ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {validationErrors.password && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {validationErrors.password}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password *
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.confirmPassword ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {validationErrors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {validationErrors.confirmPassword}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">User Groups</h3>
        <p className="text-sm text-gray-600 mb-6">
          Select at least one user group. Groups define the user's role and base permissions.
        </p>
      </div>

      {loading.groups ? (
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map(group => (
            <div
              key={group.id}
              className={`border rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                formData.groups.includes(group.id)
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => handleGroupToggle(group.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    formData.groups.includes(group.id)
                      ? 'border-blue-500 bg-blue-500'
                      : 'border-gray-300'
                  }`}>
                    {formData.groups.includes(group.id) && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <h4 className="font-medium text-gray-900">{group.name}</h4>
                    <p className="text-sm text-gray-600">{group.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    {group.permissions?.length || 0} permissions
                  </p>
                  <p className="text-xs text-gray-400">
                    {group.members?.length || 0} members
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {validationErrors.groups && (
        <p className="mt-2 text-sm text-red-600 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {validationErrors.groups}
        </p>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Additional Permissions</h3>
        <p className="text-sm text-gray-600 mb-6">
          Optionally grant additional individual permissions beyond what the user's groups provide.
          You can skip this step if the group permissions are sufficient.
        </p>
      </div>

      {loading.permissions ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 bg-gray-200 rounded"></div>
          ))}
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
          {permissions.map(permission => (
            <div
              key={permission.id}
              className={`flex items-center justify-between p-3 rounded cursor-pointer transition-colors hover:bg-gray-50 ${
                formData.additionalPermissions.includes(permission.key)
                  ? 'bg-blue-50 border border-blue-200'
                  : 'border border-transparent'
              }`}
              onClick={() => handlePermissionToggle(permission.key)}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                  formData.additionalPermissions.includes(permission.key)
                    ? 'border-blue-500 bg-blue-500'
                    : 'border-gray-300'
                }`}>
                  {formData.additionalPermissions.includes(permission.key) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <Shield className="w-4 h-4 text-gray-400" />
                <div>
                  <h5 className="font-medium text-gray-900">{permission.key}</h5>
                  <p className="text-sm text-gray-600">{permission.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Permission Summary
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                Selected groups: <strong>{formData.groups.length}</strong>
              </p>
              <p>
                Additional permissions: <strong>{formData.additionalPermissions.length}</strong>
              </p>
              <p className="mt-1">
                The user will receive all permissions from their groups plus any additional permissions selected above.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Step progress component
  const renderStepProgress = () => (
    <div className="mb-8">
      <div className="flex items-center">
        {[1, 2, 3].map((step, index) => (
          <React.Fragment key={step}>
            <div
              className={`flex items-center cursor-pointer ${
                step <= currentStep || completedSteps.includes(step - 1) ? '' : 'pointer-events-none'
              }`}
              onClick={() => handleStepClick(step)}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step === currentStep
                    ? 'bg-blue-600 text-white'
                    : completedSteps.includes(step)
                    ? 'bg-green-600 text-white'
                    : step < currentStep
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-200 text-gray-400'
                }`}
              >
                {completedSteps.includes(step) ? (
                  <Check className="w-4 h-4" />
                ) : (
                  step
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  step === currentStep ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  Step {step}
                </p>
                <p className={`text-xs ${
                  step === currentStep ? 'text-gray-600' : 'text-gray-400'
                }`}>
                  {step === 1 && 'Basic Info'}
                  {step === 2 && 'User Groups'}
                  {step === 3 && 'Permissions'}
                </p>
              </div>
            </div>
            {index < 2 && (
              <div className={`flex-1 h-px mx-4 ${
                step < currentStep || completedSteps.includes(step)
                  ? 'bg-green-300'
                  : 'bg-gray-200'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  return (
    <AppShell 
      title="Create New User" 
      subtitle="Set up a new user account with groups and permissions"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Guard permission="users.write" showMessage={true}>
          <div className="bg-white shadow-lg rounded-lg">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => navigate('/admin-iam/users')}
                  className="flex items-center text-gray-600 hover:text-gray-900"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Users
                </button>
                <div className="text-sm text-gray-500">
                  Step {currentStep} of 3
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="px-6 py-6">
              {renderStepProgress()}

              {/* Content */}
              <div className="min-h-96">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
                {currentStep === 3 && renderStep3()}
              </div>

              {/* Submit Error */}
              {validationErrors.submit && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    {validationErrors.submit}
                  </p>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </button>

                <div className="flex space-x-3">
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNext}
                      className="flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => navigate('/admin-iam/users')}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="flex items-center px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Creating...
                          </>
                        ) : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Create User
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Guard>
      </div>
    </AppShell>
  );
};

export default CreateUserPage;
